import React, { useContext, useEffect } from 'react';
import { Route, useLocation, Routes } from 'react-router-dom';
import './styles/reset.module.scss';
import './styles/deprecatedCalendarStyles.scss'; // TODO Remove when refactoring calendar
import './app.module.scss';
import '../fonts/barlow/barlow.css';
import '../fonts/merriweather.css';
import {
  BrukerApiInterface as psaUserApiInterface,
  BrukerinfoDto as PsaBrukerinfoDto,
} from './api/generated/praksissted-generated-api';
import {
  BrukerApiInterface as uhaUserApiInterface,
  BrukerinfoDto as UhaBrukerinfoDto,
} from './api/generated/studiested-generated-api';
import {
  BrukerApiInterface as studentUserApiInterface,
  BrukerinfoDto as StudentBrukerinfoDto,
} from './api/generated/student-generated-api';
import { RoleDto, RoletypeDto } from './api/generated/login-generated-api';
import {
  getLanguageFromLocalStorage,
  Language,
  LanguageContext,
} from './i18n/LanguageStore';
import { useLoader } from './api/useLoader';
import { LoginApiContext } from './api/LoginApiContext';
import { PraksisstedApiContext } from './api/PraksisstedApiContext';
import { StudentApiContext } from './api/StudentApiContext';
import { StudiestedApiContext } from './api/StudiestedApiContext';
import SelectRolePage from './login/selectRole';
import PsaRoutes from './route.psa';
import UhaRoutes from './route.uha';
import StudentRoutes from './route.student';
import AdminRoutes from './route.admin';
import UnauthenticatedRoutes from './route.unauth';
import { SelectedRoleContext, withProviders } from './providers';
import RegistrationPage from './login/registration';
import { UhaUserContext } from './user/UhaUserStore';
import { PsaUserContext } from './user/PsaUserStore';
import { StudentUserContext } from './user/StudentUserStore';
import Spinner from './components/spinner/spinner';
import { UnknownUserPage } from './login/about';
import { RenderPageNotFoundContext } from './pages/pagenotfoundStore';
import PageNotFound from './pages/pagenotfound';
import TimeoutWarning from './timeoutWarning';

type BrukerInfo = UhaBrukerinfoDto | PsaBrukerinfoDto | StudentBrukerinfoDto;

const getUpdatedSpraakvalg = (userInfo: BrukerInfo) => ({
  ...userInfo,
  innstillinger: {
    ...userInfo.innstillinger,
    spraakvalg: getLanguageFromLocalStorage(),
  },
});

const setPrefferedLanguage = function (
  psaUserApi: psaUserApiInterface,
  uhaUserApi: uhaUserApiInterface,
  studentUserApi: studentUserApiInterface,
  selectedRole: RoleDto | null | undefined,
  setSelectedLanguage: (language: Language) => void,
  selectedLanguage: Language,
  setUhaUser: (user: UhaBrukerinfoDto) => void,
  setPsaUser: (user: PsaBrukerinfoDto) => void,
  setStudentUser: (user: StudentBrukerinfoDto) => void,
) {
  if (!selectedRole) {
    return;
  }

  const setSpraak = (spraakvalg: Language) => {
    if (selectedLanguage !== spraakvalg) {
      setSelectedLanguage(spraakvalg);
    }
  };

  switch (selectedRole.type) {
    case RoletypeDto.Studiested: {
      uhaUserApi.brukerinfoGet().then((userInfo) => {
        let updatedUHUserInfo: UhaBrukerinfoDto | undefined;
        if (!userInfo.innstillinger.harGodtattBetingelser) {
          updatedUHUserInfo = getUpdatedSpraakvalg(
            userInfo,
          ) as UhaBrukerinfoDto;

          uhaUserApi
            .brukerinfoInnstillingerPut({
              brukerinnstillingerDto: {
                spraakvalg: updatedUHUserInfo.innstillinger.spraakvalg,
              },
            })
            .then();
        }

        setUhaUser(updatedUHUserInfo || userInfo);
        setSpraak(
          (updatedUHUserInfo || userInfo).innstillinger.spraakvalg as Language,
        );
      });
      break;
    }
    case RoletypeDto.Praksissted: {
      psaUserApi.brukerinfoGet().then((userInfo) => {
        let updatedPSUserInfo: PsaBrukerinfoDto | undefined;
        if (!userInfo.innstillinger.harGodtattBetingelser) {
          updatedPSUserInfo = getUpdatedSpraakvalg(
            userInfo,
          ) as PsaBrukerinfoDto;

          psaUserApi
            .brukerinfoInnstillingerPut({
              brukerinnstillingerDto: {
                spraakvalg: updatedPSUserInfo.innstillinger.spraakvalg,
              },
            })
            .then();
        }

        setPsaUser(updatedPSUserInfo || userInfo);
        setSpraak(
          (updatedPSUserInfo || userInfo).innstillinger.spraakvalg as Language,
        );
      });
      break;
    }
    case RoletypeDto.Student: {
      studentUserApi.brukerinfoGet().then((userInfo) => {
        let updatedStudentUserInfo: StudentBrukerinfoDto | undefined;
        if (!userInfo.innstillinger.harGodtattBetingelser) {
          updatedStudentUserInfo = getUpdatedSpraakvalg(
            userInfo,
          ) as StudentBrukerinfoDto;

          studentUserApi
            .brukerinfoInnstillingerPut({
              brukerinnstillingerDto: {
                spraakvalg: updatedStudentUserInfo.innstillinger.spraakvalg,
              },
            })
            .then();
        }

        setStudentUser(updatedStudentUserInfo || userInfo);
        setSpraak(
          (updatedStudentUserInfo || userInfo).innstillinger
            .spraakvalg as Language,
        );
      });
      break;
    }
    case RoletypeDto.Systemadmin: {
      setSpraak(Language.NORWEGIAN);
      break;
    }
    default:
      setSpraak(getLanguageFromLocalStorage());
  }
};

const SelectedRoleSwitch: React.FC<
  React.PropsWithChildren<{
    roleType: RoletypeDto | undefined;
  }>
> = ({ roleType }) => {
  switch (roleType) {
    case RoletypeDto.Studiested:
      return <UhaRoutes />;
    case RoletypeDto.Praksissted:
      return <PsaRoutes />;
    case RoletypeDto.Student:
      return <StudentRoutes />;
    case RoletypeDto.Systemadmin:
      return <AdminRoutes />;
    default:
      return <span>Unknown role</span>;
  }
};

const MainComponent: React.FC<
  React.PropsWithChildren<{ pageNotFound: boolean; role?: RoleDto }>
> = ({ role, pageNotFound }) => {
  if (pageNotFound) return <PageNotFound />;

  return (
    <>
      <TimeoutWarning />
      <SelectedRoleSwitch roleType={role?.type} />
    </>
  );
};

const App = () => {
  const { loginApi } = useContext(LoginApiContext);
  const { selectedLanguage, setSelectedLanguage } = useContext(LanguageContext);
  const { brukerApi: psaUserApi } = useContext(PraksisstedApiContext);
  const { brukerApi: uhaUserApi } = useContext(StudiestedApiContext);
  const { brukerApi: studentUserApi } = useContext(StudentApiContext);
  const { data, loading } = useLoader(() => loginApi.userroleGet(), [], false);
  const possibleRoles = data ? data.possibleRoles : null;
  const selectedRole = data ? data.selectedRole : undefined;
  const authenticated = data ? data.authenticated : false;
  const { setUhaUser } = useContext(UhaUserContext);
  const { setPsaUser } = useContext(PsaUserContext);
  const { setStudentUser } = useContext(StudentUserContext);
  const { shouldRender: shouldHardRenderPageNotFound } = useContext(
    RenderPageNotFoundContext,
  );
  const location = useLocation();

  useEffect(() => {
    setPrefferedLanguage(
      psaUserApi,
      uhaUserApi,
      studentUserApi,
      selectedRole,
      setSelectedLanguage,
      selectedLanguage,
      setUhaUser,
      setPsaUser,
      setStudentUser,
    );
  }, [
    uhaUserApi,
    psaUserApi,
    studentUserApi,
    selectedRole,
    setSelectedLanguage,
    selectedLanguage,
    setUhaUser,
    setPsaUser,
    setStudentUser,
  ]);

  if (loading) {
    return <Spinner />;
  }

  if (authenticated) {
    if (location.pathname.includes('/registration/')) {
      return (
        <Routes>
          <Route
            path="/registration/:inviteId"
            element={<RegistrationPage />}
          />
        </Routes>
      );
    }
  } else {
    return <UnauthenticatedRoutes />;
  }

  if (!possibleRoles || possibleRoles.length === 0) {
    return <UnknownUserPage />;
  }

  if (data && !selectedRole) {
    return <SelectRolePage userrolesDto={data} />;
  }

  return (
    <SelectedRoleContext.Provider value={selectedRole}>
      <MainComponent
        role={selectedRole}
        pageNotFound={shouldHardRenderPageNotFound}
      />
    </SelectedRoleContext.Provider>
  );
};

export default withProviders(App); // Wraps the app in the provider hierarchy defined in providers.tsx
